<template>
  <BaseModal
    color-black
    full-screen
    :is-open="showModal"
    no-close
    @close="close"
  >
    <Component
      :is="loadedLayout"
      :key="layoutKey"
      :title="modalProps.title"
    >
      <Transition name="fade" mode="out-in">
        <Component :is="loadedComponent" />
      </Transition>
    </Component>

    <!-- <UiSkeleton -->
    <!--   v-else -->
    <!--   key="skeleton" -->
    <!--   class="skeleton__item" -->
    <!--   :items="1" -->
    <!-- /> -->
  </BaseModal>
</template>

<script>
import { modalService } from './service'
import { disableScroll, enableScroll } from '@/util/scroll-lock'
import { isNullOrUndefined } from '@/util/validators/baseComponentsValidators'
import BaseModal from '@/components/Base/Modal'
const MOUNTING_DURATION_WITHOUT_SKELETON = 200

export default {
  components: {
    BaseModal,
  },
  data() {
    return {
      loadedComponent: null,
      loadedLayout: null,

      isMounted: false,

      modalProps: {
        title: '',
      },
    }
  },
  computed: {
    layoutKey() {
      return this.loadedLayout ? this.loadedLayout.name : ''
    },
    isComponentsLoaded() {
      return !!this.loadedComponent && !!this.loadedLayout
    },
    showModal() {
      return modalService.isValidQuery(this.$route.query.page)
    },
    currentModalComponent() {
      if (!this.showModal) return null
      try {
        const page = modalService.formatName(this.$route.query.page)
        return page ? () => import(`@/modal/pages/${page}`) : null
      }
      catch (e) {
        return null
      }
    },
    currentLayout() {
      if (!this.showModal) return null
      try {
        const modalComponent = this.currentModalComponent
        return modalComponent
          ? async () => {
            const modal = await modalComponent()
            if (!modal) return null

            const layoutName = modal.default.modalLayout || 'default'
            return import(`@/modal/layouts/${layoutName}`)
          }
          : null
      }
      catch (e) {
        return null
      }
    },
  },
  watch: {
    '$route.query.page': {
      immediate: true,
      handler(v) {
        this.setProps(v)
      },
    },
    showModal: {
      immediate: true,
      handler(v) {
        if (v) disableScroll()
        else enableScroll()
      },
    },
  },
  created() {
    // Close modal if no component or layout found for specified route, or layout middleware didn't pass
    this.$watch(
      () => ([this.currentModalComponent, this.currentLayout]),
      async ([modalResolver, layoutResolver]) => {
        if (!this.showModal) return
        try {
          const timeout = setTimeout(() => {
            this.isMounted = false
          }, MOUNTING_DURATION_WITHOUT_SKELETON)
          this.setComponents([null, null])
          const [modal, layout] = await this.loadComponents([modalResolver, layoutResolver])
          await this.runMiddleware(layout)
          this.setComponents([modal.default, layout.default])
          clearTimeout(timeout)
          this.isMounted = true
        }
        catch (e) {
          console.warn({ e })
          this.close()
        }
      },
      { immediate: true, deep: true })
  },
  methods: {
    close() {
      this.$modal.close()
      this.setComponents([null, null])
    },
    setProps(query) {
      const key = Object.entries(this.$pathObj).find(([_key, value]) => value.modal && value.modal === query)?.[0]
      this.modalProps.title = this.$pathObj[key]?.title ?? ''
    },
    setComponents([modal, layout]) {
      this.loadedComponent = modal
      this.loadedLayout = layout
    },
    async loadComponents([modalResolver, layoutResolver]) {
      const modal = await modalResolver()
      const layout = await layoutResolver()
      const isPresent = !isNullOrUndefined(modal) && !isNullOrUndefined(layout)
      if (!isPresent) throw new Error('Component or layout not loaded')
      return [modal, layout]
    },
    async runMiddleware(component) {
      const middleware = component.default.middleware
      if (!middleware) return
      await modalService.middlewareRunner({ middleware, context: this })
    },
  },
}
</script>
<style lang="scss" scoped>
.skeleton__item {
  --inset: 16px;
  margin: var(--inset);
  width: calc(100% - var(--inset) * 2);
  height: calc(100% - var(--inset) * 2);
}
</style>
